import React, { FunctionComponent } from 'react';
import { Box, Link, Typography } from '@mui/material';
import { useResponsive } from '../../contexts/ResponsiveContext';

interface LocationCardProps {
    locationCards: LocationCard[];
}
const commonTextStyle = {
    fontFamily: 'Poppins',
    fontWeight: 300,
    fontSize: '15px',
    lineHeight: '22px',
    color: '#191A1B',
};

interface LocationCard {
    title: string;
    freeformText1?: string;
    freeformText2?: string;
    addressLine1?: string;
    addressLine2?: string;
    addressLine3?: string;
    country: string[] | string;
    url?: string;
    phone?: string;
    email?: string;
    logo?: string;
    region?: string | string[];
}

const LocationCard: FunctionComponent<LocationCardProps> = ({ locationCards }) => {
    const { isScreenLg, isScreenMd, isScreenSm, isScreenXs } = useResponsive();
    return (
        <Box
            sx={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent:
                    isScreenSm || isScreenXs
                        ? 'center'
                        : isScreenMd
                          ? 'space-between'
                          : 'space-around',
                // padding: '2rem',
                width: '100%',
                boxSizing: 'border-box',
                flexDirection: 'row',
                overflowY: 'hidden',
                backgroundColor: 'white',
            }}
        >
            {locationCards.map((office, index) => (
                <Box
                    key={index}
                    sx={{
                        width: isScreenLg ? '275px' : isScreenMd ? '315px' : '356px',
                        minHeight: '286px',
                        backgroundColor: 'white',
                        border: '1px solid #e0e0e0',
                        borderRadius: '1px',
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                        gap: '0.55rem',
                        marginBottom: '1rem',
                        padding: '0.5rem 0.5rem 1.5rem 0.5rem',
                        boxSizing: 'border-box',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        {office.url ? (
                            <Link
                                href={office.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                underline="none"
                                sx={{ color: '#C30101' }}
                            >
                                <Typography
                                    component="div"
                                    sx={{
                                        fontFamily: 'Poppins',
                                        fontSize: '16px',
                                        fontWeight: '500',
                                    }}
                                    gutterBottom
                                >
                                    {office.title}
                                    <Typography sx={{ ...commonTextStyle, marginRight: '4px' }}>
                                        {office.freeformText1}
                                    </Typography>
                                </Typography>
                            </Link>
                        ) : (
                            <Typography
                                component="div"
                                sx={{
                                    fontFamily: 'Poppins',
                                    color: '#C30101',
                                    fontSize: '16px',
                                    fontWeight: '500',
                                }}
                                gutterBottom
                            >
                                {office.title}
                                <Typography sx={{ ...commonTextStyle, marginRight: '4px' }}>
                                    {office.freeformText1}
                                </Typography>
                            </Typography>
                        )}

                        {office.logo &&
                            (office.url ? (
                                <Link
                                    href={office.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{ display: 'inline-block' }}
                                >
                                    <img
                                        src={office.logo}
                                        alt=""
                                        style={{ maxWidth: '120px', maxHeight: '45px' }}
                                    />
                                </Link>
                            ) : (
                                <img
                                    src={office.logo}
                                    alt=""
                                    style={{ maxWidth: '120px', maxHeight: '45px' }}
                                />
                            ))}
                    </Box>

                    <Box>
                        <Typography sx={commonTextStyle}>{office.freeformText2}</Typography>
                        <Box sx={{ height: '8px' }} />
                        <Typography sx={commonTextStyle}>{office.addressLine1}</Typography>
                        <Typography sx={commonTextStyle}>{office.addressLine2}</Typography>
                        <Typography sx={commonTextStyle}>{office.addressLine3}</Typography>
                        <Box sx={{ height: '4px' }} />
                        <Typography sx={commonTextStyle}>{office.country}</Typography>
                    </Box>
                    <Box sx={{ height: '8px' }} />
                    <Box>
                        <Typography sx={commonTextStyle}>
                            <strong>Phone:</strong> {office.phone}
                        </Typography>
                        {office.email && (
                            <Typography sx={commonTextStyle}>
                                <strong>Email:</strong>{' '}
                                <Link
                                    href={`mailto:${office.email}`}
                                    sx={{
                                        color: '#191A1B',
                                        textDecoration: 'none',
                                        '&:hover': {
                                            textDecoration: 'underline',
                                        },
                                    }}
                                >
                                    {office.email}
                                </Link>
                            </Typography>
                        )}
                    </Box>
                </Box>
            ))}
        </Box>
    );
};

export default LocationCard;
